import { HttpErrorResponse } from '@angular/common/http';
import { type ErrorHandler } from '@angular/core';
import * as sentry from '@sentry/angular';
import { Guid } from 'guid-typescript';

/**
 * Groups status-0 HTTP errors (network failure, blocked or CORS-less response) into one Sentry issue per endpoint
 * instead of one catch-all issue. The Sentry error handler captures only the message of an `HttpErrorResponse`, so a
 * `beforeSend` never sees the URL; the fingerprint is therefore set on a scope around the capture.
 */
export class SentryHttpErrorHandler implements ErrorHandler {
  private readonly sentryErrorHandler = sentry.createErrorHandler();

  handleError(error: unknown): void {
    const isNetworkError = error instanceof HttpErrorResponse && error.status === 0;
    const endpoint = isNetworkError ? toEndpoint(error.url) : null;

    if (!endpoint) {
      this.sentryErrorHandler.handleError(error);
      return;
    }

    // Sentry captures synchronously inside handleError, so the scope applies to this event only.
    sentry.withScope(scope => {
      scope.setFingerprint(['http-status-0', endpoint.host, endpoint.path]);
      scope.setLevel('warning');
      scope.setTags({ 'http.host': endpoint.host, 'http.path': endpoint.path });
      this.sentryErrorHandler.handleError(error);
    });
  }
}

/** Returns the host and the path without query string, with GUID and integer segments replaced by `:id`. */
function toEndpoint(url: string | null): { host: string; path: string } | null {
  if (!url) {
    return null;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url, window.location.origin);
  } catch {
    return null;
  }

  const path = parsedUrl.pathname
    .split('/')
    .map(x => (isIdSegment(x) ? ':id' : x))
    .join('/');

  return { host: parsedUrl.host, path };
}

function isIdSegment(segment: string): boolean {
  const isInteger = segment !== '' && [...segment].every(x => x >= '0' && x <= '9');

  return isInteger || Guid.isGuid(segment);
}
